import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { Close, ExpandMore, ExpandLess } from '@material-ui/icons'
import { CustomSelect } from '../../../../components/form'
import { Preloader } from '../../../../components/UI'
import styles from './styles.module.scss'

export const ReusableFilters = ({
  filterConfigs = [],
  defaultValues = {},
  onFiltersChange,
  isLoading = false,
  title = 'calendar.filters',
}) => {
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false)
  const [currentValues, setCurrentValues] = useState(defaultValues)

  const defaultValuesStringified = JSON.stringify(defaultValues)

  useEffect(() => {
    setCurrentValues(defaultValues)
  }, [defaultValuesStringified, defaultValues])

  const handleFilterChange = useCallback(
    (filterKey, value) => {
      const config = filterConfigs.find(c => c.key === filterKey)
      const option = config?.options?.find(opt => opt.id === value)

      const newValues = { ...currentValues, [filterKey]: option || null }
      setCurrentValues(newValues)
      onFiltersChange(newValues)
    },
    [filterConfigs, currentValues, onFiltersChange]
  )

  const clearFilters = useCallback(() => {
    const clearedValues = {}
    filterConfigs.forEach(config => {
      clearedValues[config.key] = null
    })
    setCurrentValues(clearedValues)
    onFiltersChange(clearedValues)
  }, [filterConfigs, onFiltersChange])

  const toggleFilters = useCallback(() => {
    setIsFiltersCollapsed(!isFiltersCollapsed)
  }, [isFiltersCollapsed])

  const getActiveFilters = useCallback(() => {
    const activeFilters = []

    filterConfigs.forEach(config => {
      const value = currentValues[config.key]
      if (value) {
        activeFilters.push({
          key: config.key,
          label: value.value || value.title || value.label || config.label,
          value: value,
          onRemove: () => handleFilterChange(config.key, null),
        })
      }
    })

    return activeFilters
  }, [filterConfigs, currentValues, handleFilterChange])

  const activeFilters = useMemo(() => getActiveFilters(), [getActiveFilters])

  const filtersContentClass = useMemo(
    () =>
      `${styles.filtersContent} ${isFiltersCollapsed ? styles.collapsed : ''}`,
    [isFiltersCollapsed]
  )

  const filtersContentWrapperClass = useMemo(
    () =>
      `${styles.filtersContentWrapper} ${isFiltersCollapsed ? styles.collapsed : ''}`,
    [isFiltersCollapsed]
  )

  const showActiveFilters = useMemo(
    () => isFiltersCollapsed && activeFilters.length > 0,
    [isFiltersCollapsed, activeFilters.length]
  )

  const toggleIcon = useMemo(
    () => (isFiltersCollapsed ? <ExpandMore /> : <ExpandLess />),
    [isFiltersCollapsed]
  )

  const createFilterChangeHandler = useCallback(
    configKey => val => handleFilterChange(configKey, val),
    [handleFilterChange]
  )

  return (
    <div className={styles.sidebar} data-testid='filters-sidebar'>
      <div className={styles.filters}>
        <div className={styles.filtersHeader}>
          <div className={styles.filtersTitleContainer}>
            <h3 className={styles.filtersTitle}>
              <FormattedMessage id={title} />
            </h3>
          </div>
          <button
            className={styles.toggleButton}
            onClick={toggleFilters}
            aria-label='Toggle filters'
            data-testid='toggle-filters-button'
          >
            {toggleIcon}
          </button>
        </div>

        {showActiveFilters && (
          <div
            className={styles.activeFiltersContainer}
            data-testid='active-filters'
          >
            <div className={styles.activeFilters}>
              {activeFilters.map((filter, index) => (
                <div
                  key={`${filter.key}-${index}`}
                  className={styles.filterChip}
                  data-testid={`active-filter-${filter.key}`}
                >
                  <span className={styles.filterChipLabel}>{filter.label}</span>
                </div>
              ))}
            </div>
            <button
              className={styles.clearAllButton}
              onClick={clearFilters}
              aria-label='Reset all filters'
              data-testid='clear-all-filters-button'
            >
              Reset
            </button>
          </div>
        )}

        <div className={filtersContentClass}>
          <div className={filtersContentWrapperClass}>
            {filterConfigs.map(config => (
              <div key={config.key} className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <FormattedMessage id={config.label} />
                </label>
                <CustomSelect
                  onChange={createFilterChangeHandler(config.key)}
                  options={config.options || []}
                  label={config.label}
                  defaultValue={currentValues[config.key]}
                  testId={`filter-${config.key}`}
                />
              </div>
            ))}

            <button
              className={styles.clearButton}
              onClick={clearFilters}
              data-testid='clear-filters-button'
            >
              <Close />
              <span className={styles.clearButtonText}>
                <FormattedMessage id='ratings.clearFilters' />
              </span>
            </button>
          </div>

          {isLoading && (
            <div className={styles.filtersLoaderOverlay}>
              <Preloader />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ReusableFilters
